import { createContext, useContext, useEffect, useState } from "react";
import {
  METRICS_HISTORY_API,
  METRICS_HISTORY_AVAILABLE_MONTHS_API,
  METRICS_HISTORY_UPSERT_API,
  REGION_METRICS_HISTORY_API,
} from "../config/apiConfig";
import { securedFetch } from "../utils/api";
import { useUserContext } from "./UserContext";

const TreasuryKRISummaryContext = createContext();

export const TreasuryKRISummaryProvider = ({ children }) => {
  const [selectedPresetDateRange, setSelectedPresetDateRange] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedMonthEnd, setSelectedMonthEnd] = useState(null);
  const [selectedBusinessEntityID, setSelectedBusinessEntityID] = useState();
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [
    metricsHistoryAvailableMonthsLoading,
    setMetricsHistoryAvailableMonthsLoading,
  ] = useState(false);
  const [
    metricsHistoryAvailableMonthsError,
    setMetricsHistoryAvailableMonthsError,
  ] = useState(null);
  const [metricsHistoryAvailableMonths, setMetricsHistoryAvailableMonths] =
    useState([]);

  const [hrMetricsHistoryLoading, setHrMetricsHistoryLoading] = useState(false);
  const [hrMetricsHistoryError, setHrMetricsHistoryError] = useState(null);
  const [hrMetricsHistory, setHrMetricsHistory] = useState([]);

  const [regionMetricsHistoryLoading, setRegionMetricsHistoryLoading] =
    useState(false);
  const [regionMetricsHistoryError, setRegionMetricsHistoryError] =
    useState(null);
  const [regionMetricsHistory, setRegionMetricsHistory] = useState([]);

  const [
    metricsHistoryUpsertAndSaveLoading,
    setMetricsHistoryUpsertAndSaveLoading,
  ] = useState(false);
  const [
    metricsHistoryUpsertAndSaveError,
    setMetricsHistoryUpsertAndSaveError,
  ] = useState(null);

  const { loggedInUserEmail } = useUserContext();

  // NOTE: Global only for Global Saved History datas and IsKRISummary for provide only what to be shown in KRI Page
  const fetchHrMetricsHistoryData = ({ isGlobal, isKRISummary } = {}) => {
    setHrMetricsHistoryLoading(true);
    setHrMetricsHistoryError(null);

    const payload = {
      businessEntityID: selectedBusinessEntityID,
      isGlobal,
      isKRISummary,
    };
    // include montheEnd if its there else include the ranges
    if (selectedMonthEnd) {
      payload.monthEnd = selectedMonthEnd?.format("YYYY-MM-DD");
    } else {
      payload.startDateRange = selectedStartDate?.format("YYYY-MM-DD");
      payload.endDateRange = selectedEndDate?.format("YYYY-MM-DD");
    }

    securedFetch(METRICS_HISTORY_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch Hedge Ratio Metrics History");
        }
        return response.json();
      })
      .then((data) => {
        if (data) {
          setHrMetricsHistory(data);
        } else {
          throw new Error("Failed to fetch Hedge Ratio Metrics History");
        }
      })
      .catch((error) => {
        setHrMetricsHistoryError(error.message);
        alert(error.message);
      })
      .finally(() => {
        setHrMetricsHistoryLoading(false);
      });
  };

  // API to fetch the metric history for all the SFC's
  const fetchRegionMetricsHistoryData = ({
    isGlobal,
    isKRISummary,
    currency,
  } = {}) => {
    setRegionMetricsHistoryLoading(true);
    setRegionMetricsHistoryError(null);

    const payload = {
      isGlobal,
      isKRISummary,
      currency,
    };

    if (selectedMonthEnd) {
      payload.monthEnd = selectedMonthEnd?.format("YYYY-MM-DD");
    } else {
      payload.startDateRange = selectedStartDate?.format("YYYY-MM-DD");
      payload.endDateRange = selectedEndDate?.format("YYYY-MM-DD");
    }

    securedFetch(REGION_METRICS_HISTORY_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch Region Metrics History");
        }
        return response.json();
      })
      .then((data) => {
        if (data) {
          setRegionMetricsHistory(data);
        } else {
          throw new Error("Failed to fetch Region Metrics History");
        }
      })
      .catch((error) => {
        setRegionMetricsHistoryError(error.message);
        alert(error.message);
      })
      .finally(() => {
        setRegionMetricsHistoryLoading(false);
      });
  };

  const metricsHistoryUpsertAndSave = (items = []) => {
    setMetricsHistoryUpsertAndSaveLoading(true);
    setMetricsHistoryUpsertAndSaveError(null);

    const promises = items.map(
      ({
        dimension,
        dimension2,
        measure,
        businessEntityID,
        value,
        modelDatabase,
        monthEnd,
        isGlobal = false,
      }) =>
        securedFetch(METRICS_HISTORY_UPSERT_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            dimension,
            dimension2,
            measure,
            businessEntityID,
            modelDatabase,
            monthEnd,
            value,
            isGlobal,
            email: loggedInUserEmail,
          }),
        }).then((response) => {
          if (!response.ok) {
            throw new Error("Failed to Save Metrics History Data");
          }
          return response.json();
        }),
    );

    return Promise.all(promises)
      .then((results) => {
        if (results.every((data) => data)) {
          alert("All Metrics History Saved Successfully!");
          return results;
        } else {
          throw new Error("Failed to Save Some Metrics History Data");
        }
      })
      .catch((error) => {
        setMetricsHistoryUpsertAndSaveError(error.message);
        alert(error.message);
        throw error;
      })
      .finally(() => {
        setMetricsHistoryUpsertAndSaveLoading(false);
      });
  };

  const fetchMetricsHistoryAvailableMonths = () => {
    setMetricsHistoryAvailableMonthsLoading(true);
    setMetricsHistoryAvailableMonthsError(null);
    securedFetch(METRICS_HISTORY_AVAILABLE_MONTHS_API, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch Metrics History Available Months");
        }
        return response.json();
      })
      .then((data) => {
        if (data) {
          setMetricsHistoryAvailableMonths(data);
        } else {
          throw new Error("Failed to fetch Metrics History Available Months");
        }
      })
      .catch((error) => {
        setMetricsHistoryAvailableMonthsError(error.message);
        alert(error.message);
      })
      .finally(() => {
        setMetricsHistoryAvailableMonthsLoading(false);
      });
  };

  useEffect(() => {
    // Not used so far
    if (false) {
      fetchMetricsHistoryAvailableMonths();
    }
  }, []);

  return (
    <TreasuryKRISummaryContext.Provider
      value={{
        selectedBusinessEntityID,
        setSelectedBusinessEntityID,
        selectedPresetDateRange,
        setSelectedPresetDateRange,
        selectedStartDate,
        setSelectedStartDate,
        selectedEndDate,
        setSelectedEndDate,
        selectedMonthEnd,
        setSelectedMonthEnd,
        selectedCurrency,
        setSelectedCurrency,
        metricsHistoryAvailableMonthsLoading,
        metricsHistoryAvailableMonthsError,
        metricsHistoryAvailableMonths,
        hrMetricsHistoryLoading,
        setHrMetricsHistoryLoading,
        hrMetricsHistoryError,
        setHrMetricsHistoryError,
        hrMetricsHistory,
        setHrMetricsHistory,

        regionMetricsHistoryLoading,
        setRegionMetricsHistoryLoading,
        regionMetricsHistoryError,
        setRegionMetricsHistoryError,
        regionMetricsHistory,
        setRegionMetricsHistory,
        fetchRegionMetricsHistoryData,

        fetchHrMetricsHistoryData,
        metricsHistoryUpsertAndSaveLoading,
        metricsHistoryUpsertAndSaveError,
        metricsHistoryUpsertAndSave,
      }}
    >
      {children}
    </TreasuryKRISummaryContext.Provider>
  );
};

export const useTreasuryKRISummaryContext = () =>
  useContext(TreasuryKRISummaryContext);
